import { useState, useEffect } from 'react';
import { Cookie, X, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface CookieConsentBannerProps {
    onAccept: () => void;
    onDecline: () => void;
    onVisibilityChange?: (visible: boolean) => void;
}

export function CookieConsentBanner({ onAccept, onDecline, onVisibilityChange }: CookieConsentBannerProps) {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Check if consent was already given
        const consent = localStorage.getItem('noknok_tracking_consent');
        if (consent === null) {
            // Delay appearance for better UX
            const timer = setTimeout(() => {
                setIsVisible(true);
                onVisibilityChange?.(true);
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [onVisibilityChange]);

    const handleAccept = () => {
        setIsVisible(false);
        onVisibilityChange?.(false);
        onAccept();
    };

    const handleDecline = () => {
        setIsVisible(false);
        onVisibilityChange?.(false);
        onDecline();
    };

    const handleDismiss = () => {
        setIsVisible(false);
        onVisibilityChange?.(false);
    };

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-4 left-4 right-4 sm:right-auto sm:max-w-sm z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
            <div className="relative bg-white border border-gray-200 shadow-xl rounded-2xl p-4 pr-9">
                {/* Close X — top-right corner of the card */}
                <button
                    onClick={handleDismiss}
                    className="absolute top-2.5 right-2.5 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                    aria-label="Close"
                >
                    <X className="w-4 h-4" />
                </button>

                {/* Icon + copy */}
                <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Cookie className="w-5 h-5 text-primary" />
                    </div>
                    <p className="text-sm text-gray-600 leading-snug">
                        We value your privacy. We use cookies to improve your experience.{' '}
                        <a href="/privacy" className="text-primary hover:underline">Learn more</a>
                    </p>
                </div>

                {/* Buttons */}
                <div className="flex items-center gap-2 mt-3">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDecline}
                        className="text-xs h-8 px-3 flex-1"
                    >
                        Maybe Later
                    </Button>
                    <Button
                        size="sm"
                        onClick={handleAccept}
                        className="text-xs h-8 px-4 gap-1 flex-1"
                    >
                        <Rocket className="w-3.5 h-3.5" />
                        Let's Roll
                    </Button>
                </div>
            </div>
        </div>
    );
}
